import {
  ADD_FORM_FIELD_TEXT,
  ADD_FORM_FIELD_EMAIL,
  ADD_FORM_FIELD_PHONE,
  ADD_FORM_FIELD_DATE,
  ADD_FORM_FIELD_MULTI_LINE_TEXT,
  ADD_FORM_FIELD_DROPDOWN,
  ADD_FORM_FIELD_CHECKBOX,
  ADD_FORM_FIELD_RADIO_SET,
} from "./ActionConstants";
import AppConstants from "./AppConstants";
import { FORM_CHECKBOX, FORM_INPUT, FORM_RADIO, FORM_SELECT } from "./DataTypes";

const { LINK_TYPE_BLOCK, VALUE_THIS } = AppConstants;

/** @type {"field"} */
export const FORM_STYLE_FIELD = "field";
/** @type {"dropdown"} */
export const FORM_STYLE_DROPDOWN = "dropdown";
/** @type {"radios"} */
export const FORM_STYLE_RADIOS = "radios";
/** @type {"checkboxes"} */
export const FORM_STYLE_CHECKBOXES = "checkboxes";
/**
 * Design Settings form style ids: each one names a `theme.styles` entry.
 * @typedef {typeof FORM_STYLE_FIELD | typeof FORM_STYLE_DROPDOWN | typeof FORM_STYLE_RADIOS | typeof FORM_STYLE_CHECKBOXES} FormStyleId
 */

/**
 * Every Design Settings form style, base entry first.
 * @type {FormStyleId[]}
 */
export const FORM_STYLE_IDS = [
  FORM_STYLE_FIELD,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_RADIOS,
  FORM_STYLE_CHECKBOXES,
];

/** @type {"default"} */
export const FORM_STATE_DEFAULT = "default";
/** @type {"focus"} */
export const FORM_STATE_FOCUS = "focus";
/** @type {"error"} */
export const FORM_STATE_ERROR = "error";
/**
 * States a form field is styled in. Default is the base props; the others sit under their own
 * key.
 * @typedef {typeof FORM_STATE_DEFAULT | typeof FORM_STATE_FOCUS | typeof FORM_STATE_ERROR} FormState
 */

/** @type {"above"} */
export const FORM_FIELD_LABEL_POSITION_ABOVE = "above";
/** @type {"inline"} */
export const FORM_FIELD_LABEL_POSITION_INLINE = "inline";
/**
 * Values of `label.position`.
 * @typedef {typeof FORM_FIELD_LABEL_POSITION_ABOVE | typeof FORM_FIELD_LABEL_POSITION_INLINE} FormFieldLabelPosition
 */

/** @type {"after"} */
export const FORM_FIELD_REQUIRED_PLACEMENT_AFTER = "after";
/** @type {"before"} */
export const FORM_FIELD_REQUIRED_PLACEMENT_BEFORE = "before";
/**
 * Values of `label.requiredPlacement`.
 * @typedef {typeof FORM_FIELD_REQUIRED_PLACEMENT_AFTER | typeof FORM_FIELD_REQUIRED_PLACEMENT_BEFORE} FormFieldRequiredPlacement
 */

/** @type {"solid"} */
export const FORM_FIELD_BORDER_STYLE_SOLID = "solid";
/** @type {"dashed"} */
export const FORM_FIELD_BORDER_STYLE_DASHED = "dashed";
/** @type {"dotted"} */
export const FORM_FIELD_BORDER_STYLE_DOTTED = "dotted";
/** @type {"none"} */
export const FORM_FIELD_BORDER_STYLE_NONE = "none";
/**
 * Values of `control.bS`.
 * @typedef {typeof FORM_FIELD_BORDER_STYLE_SOLID | typeof FORM_FIELD_BORDER_STYLE_DASHED | typeof FORM_FIELD_BORDER_STYLE_DOTTED | typeof FORM_FIELD_BORDER_STYLE_NONE} FormFieldBorderStyle
 */

/** @type {"chevron"} */
export const FORM_FIELD_ARROW_STYLE_CHEVRON = "chevron";
/** @type {"caret"} */
export const FORM_FIELD_ARROW_STYLE_CARET = "caret";
/** @type {"none"} */
export const FORM_FIELD_ARROW_STYLE_NONE = "none";
/**
 * Values of `control.arrow.style`.
 * @typedef {typeof FORM_FIELD_ARROW_STYLE_CHEVRON | typeof FORM_FIELD_ARROW_STYLE_CARET | typeof FORM_FIELD_ARROW_STYLE_NONE} FormFieldArrowStyle
 */

/** @type {"vertical"} */
export const FORM_FIELD_RADIO_LAYOUT_VERTICAL = "vertical";
/** @type {"horizontal"} */
export const FORM_FIELD_RADIO_LAYOUT_HORIZONTAL = "horizontal";
/**
 * Values of `radio.direction`.
 * @typedef {typeof FORM_FIELD_RADIO_LAYOUT_VERTICAL | typeof FORM_FIELD_RADIO_LAYOUT_HORIZONTAL} FormFieldRadioLayout
 */

/** @type {"filled"} */
export const FORM_FIELD_CHECKED_STYLE_FILLED = "filled";
/** @type {"outline"} */
export const FORM_FIELD_CHECKED_STYLE_OUTLINE = "outline";
/**
 * Values of `control.radio.style`.
 * @typedef {typeof FORM_FIELD_CHECKED_STYLE_FILLED | typeof FORM_FIELD_CHECKED_STYLE_OUTLINE} FormFieldRadioStyle
 */

/** @type {"checkmark"} */
export const FORM_FIELD_CHECKED_STYLE_CHECKMARK = "checkmark";
/** @type {"x"} */
export const FORM_FIELD_CHECKED_STYLE_X = "x";
/**
 * Values of `control.checkbox.style`.
 * @typedef {typeof FORM_FIELD_CHECKED_STYLE_CHECKMARK | typeof FORM_FIELD_CHECKED_STYLE_X} FormFieldCheckboxStyle
 */

/**
 * A form field is one of four element types — `input`, `select`, `radio`, `checkbox` — with no
 * shared parent type and no discriminator, so the element type is the field type.
 * @type {import("./DataTypes").FormFieldType[]}
 */
export const FORM_FIELD_ELEMENT_TYPES = [FORM_INPUT, FORM_SELECT, FORM_CHECKBOX, FORM_RADIO];

/**
 * @param {string} type - An element's `type`.
 * @returns {boolean}
 */
export const isFormFieldElementType = (type) => FORM_FIELD_ELEMENT_TYPES.includes(type);

/**
 * The Design Settings form style each type's top-level `style` points at; merge-utils inherits
 * every unset visual prop through it.
 * @type {Record<string, FormStyleId>}
 */
export const FORM_FIELD_STYLE_BY_TYPE = {
  [FORM_INPUT]: FORM_STYLE_FIELD,
  [FORM_SELECT]: FORM_STYLE_DROPDOWN,
  [FORM_RADIO]: FORM_STYLE_RADIOS,
  [FORM_CHECKBOX]: FORM_STYLE_CHECKBOXES,
};

/** Data types, `input` elements only. */
export const FORM_FIELD_DATA_TYPE_TEXT = "text";
export const FORM_FIELD_DATA_TYPE_EMAIL = "email";
export const FORM_FIELD_DATA_TYPE_TEL = "tel";
export const FORM_FIELD_DATA_TYPE_DATE = "date";

const DEFAULT_OPTIONS = ["Option 1", "Option 2"];

/**
 * The content a newly inserted field starts with, per element type: only the keys that type uses.
 * Clone before use — the options array is shared.
 * @type {Record<string, { label: string, placeholder?: string, options?: string[] }>}
 */
export const SEEDED_FORM_FIELD_CONTENT = {
  [FORM_INPUT]: { label: "Label", placeholder: "Placeholder" },
  [FORM_SELECT]: { label: "Label", placeholder: "Placeholder", options: DEFAULT_OPTIONS },
  [FORM_RADIO]: { label: "Label", options: DEFAULT_OPTIONS },
  [FORM_CHECKBOX]: { label: "Label" },
};

/**
 * Label and placeholder an `input` starts with, per `dataType`, laid over its type's seed.
 * @type {Record<string, { label: string, placeholder: string }>}
 */
export const SEEDED_FORM_INPUT_CONTENT = {
  [FORM_FIELD_DATA_TYPE_TEXT]: SEEDED_FORM_FIELD_CONTENT[FORM_INPUT],
  [FORM_FIELD_DATA_TYPE_EMAIL]: { label: "Email", placeholder: "you@example.com" },
  [FORM_FIELD_DATA_TYPE_TEL]: { label: "Phone", placeholder: "(555) 123-4567" },
  [FORM_FIELD_DATA_TYPE_DATE]: { label: "Date", placeholder: "mm/dd/yyyy" },
};

/** Box widths every form field starts at, per layout. */
export const FORM_FIELD_WIDTHS = { desktop: 300, mobile: 220 };
/** Box heights a `checkbox` starts at, per layout. */
export const FORM_FIELD_CHECKBOX_HEIGHTS = { desktop: 45, mobile: 42 };

/** Box heights an `input` starts at, per layout. */
export const FORM_FIELD_INPUT_HEIGHTS = { desktop: 80, mobile: 80 };
export const FORM_FIELD_MULTILINE_HEIGHTS = { desktop: 180, mobile: 140 };

/** The Submit Button's own `label` — the text a visitor reads on the button. */
export const SUBMIT_BUTTON_TEXT = "Submit";

/** The `link` a Submit Button starts with: submit the form and stay on the current canvas. */
export const SUBMIT_FORM_LINK = {
  type: LINK_TYPE_BLOCK,
  target: VALUE_THIS,
  subForm: 1,
  noScroll: 1,
};

/**
 * @typedef {Object} FormFieldMenuItem
 * @property {string} id - Stable slug; builds test ids.
 * @property {string} action - The context menu action that adds this field.
 * @property {string} icon
 * @property {string} label
 * @property {{ type: string, dataType?: string, multiLine?: boolean }} field - `getFormFieldElement` args.
 */

/**
 * The field options every Add Element menu offers, in menu order. The Submit Button follows them.
 * @type {FormFieldMenuItem[]}
 */
export const FORM_FIELD_MENU_ITEMS = [
  {
    id: "text",
    action: ADD_FORM_FIELD_TEXT,
    icon: "formFieldText",
    label: "Text",
    field: { type: FORM_INPUT, dataType: FORM_FIELD_DATA_TYPE_TEXT },
  },
  {
    id: "email",
    action: ADD_FORM_FIELD_EMAIL,
    icon: "formFieldTextEmail",
    label: "Email",
    field: { type: FORM_INPUT, dataType: FORM_FIELD_DATA_TYPE_EMAIL },
  },
  {
    id: "phone",
    action: ADD_FORM_FIELD_PHONE,
    icon: "formFieldTextPhone",
    label: "Phone",
    field: { type: FORM_INPUT, dataType: FORM_FIELD_DATA_TYPE_TEL },
  },
  {
    id: "date",
    action: ADD_FORM_FIELD_DATE,
    icon: "formFieldTextDate",
    label: "Date",
    field: { type: FORM_INPUT, dataType: FORM_FIELD_DATA_TYPE_DATE },
  },
  {
    id: "multi-line-text",
    action: ADD_FORM_FIELD_MULTI_LINE_TEXT,
    icon: "formFieldTextMultiLine",
    label: "Multi-Line Text",
    field: { type: FORM_INPUT, multiLine: true },
  },
  {
    id: "dropdown",
    action: ADD_FORM_FIELD_DROPDOWN,
    icon: "formFieldSelect",
    label: "Dropdown",
    field: { type: FORM_SELECT },
  },
  {
    id: "checkbox",
    action: ADD_FORM_FIELD_CHECKBOX,
    icon: "formFieldCheckbox",
    label: "Checkbox",
    field: { type: FORM_CHECKBOX },
  },
  {
    id: "radio-set",
    action: ADD_FORM_FIELD_RADIO_SET,
    icon: "formFieldRadioSet",
    label: "Radio Set",
    field: { type: FORM_RADIO },
  },
];

/** The Submit Button entry that follows `FORM_FIELD_MENU_ITEMS` in each Add Element menu. */
export const FORM_SUBMIT_MENU_ITEM = { icon: "buttonElement", label: "Submit Button" };
